import React, { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { useSelector } from "react-redux";
import { ProductFruits } from "react-product-fruits";
import { usePostHog } from "posthog-js/react";

import "react-toastify/dist/ReactToastify.css";

import {
  INTERCOM_APP_ID,
  PRODUCT_FRUITS_WORKSPACE_CODE,
  PRODUCT_FRUITS_LANGUAGE,
} from "./constants";
import { PageLoader } from "./components/loader/PageLoader";
import { CookieConsentModal } from "./components/modals/cookieConsentModal";
import { ElementObserver } from "./components/observers";
import { AppRouterProvider } from "./router";
import { selectHideCookieConsent, selectLoading } from "./store/reducers/ui";
import { useGetUserQuery } from "./store/services/user";
import { showCookieConsentAction } from "./utils/dom";
import { isMobileMedia } from "./utils/media";
// import * as Sentry from "@sentry/react";
// import { AskVideoModal } from "./components/modals";

const contextClass = {
  success: "bg-green-500",
  error: "bg-orange-500",
  info: "bg-blue-500",
  warning: "bg-brown-500",
  default: "bg-gray-500",
};

function App() {
  const isLoading = useSelector(selectLoading);
  const hideCookieConsent = useSelector(selectHideCookieConsent);
  // const videoAsk = useSelector(selectVideoAsk);
  const { data } = useGetUserQuery();
  const posthog = usePostHog();
  const _window = window as any;
  // Not using Sentry Feedback widget atm. since 2nd april 2025
  // const feedback = Sentry.feedbackIntegration({
  //   autoInject: false,
  //   showBranding: false,
  //   triggerLabel: "Send Feedback",
  //   formTitle: "Send Us Your Feedback",
  //   submitButtonLabel: "Send Report",
  //   messagePlaceholder:
  //     "What's your feedback or bug report? What did you expect?",
  // });

  useEffect(() => {
    if (
      _window.Intercom &&
      _window.location.pathname !== "/transfer-calculator"
    ) {
      _window.Intercom("boot", {
        app_id: INTERCOM_APP_ID,
        email: data?.email,
        name: data?.first_name,
        custom_launcher_selector: ".contact-support-btn",
        user_id: data?.id,
        user_hash: data?.intercom_user_hash,
      });
    }
  }, [
    _window,
    _window.Intercom,
    data?.email,
    data?.first_name,
    data?.id,
    data?.intercom_user_hash,
  ]);

  // Not using Sentry Feedback widget atm. since 2nd april 2025
  // useEffect(() => {
  //   if (
  //     _window &&
  //     _window.location &&
  //     _window.location.pathname !== "/transfer-calculator"
  //   ) {
  //     feedback.createWidget();
  //     Sentry.setUser({ id: data?.ga_id });
  //   }
  // }, [_window, feedback, data?.ga_id]);

  useEffect(() => {
    if (_window) {
      _window._mfq = _window._mfq || [];
      _window._mfq.push(["identify", data?.ga_id]);
      _window._mfq.push(["setVariable", "userId", data?.ga_id]);
    }
  }, [_window, data?.ga_id]);

  useEffect(() => {
    showCookieConsentAction(!hideCookieConsent);
  }, [hideCookieConsent]);

  useEffect(() => {
    // Test PostHog event
    if (data?.ga_id && posthog) {
      posthog.identify(data.ga_id, {
        email: data.email,
        name: data.contact_name,
      });
    }
  }, [data?.email, data?.contact_name, data?.ga_id, posthog]);

  return (
    <>
      {isLoading && <PageLoader />}

      <AppRouterProvider />

      {data ? (
        <ProductFruits
          workspaceCode={PRODUCT_FRUITS_WORKSPACE_CODE}
          language={PRODUCT_FRUITS_LANGUAGE}
          user={{
            username: data?.ga_id || "",
            email: data?.email || "",
          }}
        />
      ) : null}

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="colored"
        className="md:w-[592px]"
        toastClassName={(args) =>
          contextClass[args?.type || "default"] +
          " relative flex px-6 py-4 min-h-10 rounded-lg justify-between overflow-hidden cursor-pointer md:w-[592px] font-poppins text-base font-bold toast"
        }
      />

      {!hideCookieConsent && <CookieConsentModal />}

      <ElementObserver
        targetSelector="#cc-preferences-button"
        callback={() => showCookieConsentAction(false)}
        disabled={!isMobileMedia()}
      />

      {/* {videoAsk && videoAsk.showAction && videoAsk.url && (
        <AskVideoModal url={videoAsk.url} expanded={true} />
      )} */}
    </>
  );
}

export default App;
